import { defineMessages } from 'react-intl';
//
// - defaultMessage: The user-facing text, rendered in the user's language.
//   Also serves as the English base for external translators.
// - description: Context note for translators (never rendered).
//   Helps them understand where/how the message appears.
export const notificationDescriptionMessages = defineMessages({
  // === Order Alerts ===
  ORDER_ALERT_COMBINE_ORDERS: {
    id: 'TisZEd',
    defaultMessage: '{combineOrderCount} open orders for {customerName}. Consider combining these orders to save on shipping.',
    description: 'Notification hub description: orders can be combined — shows count and customer name',
  },
  ORDER_ALERT_NEW_PRODUCTS: {
    id: 'a4nOzK',
    defaultMessage:
      '{newProductCount} new products have recently been created. You may want to configure these products with default shipping information for future orders.',
    description: 'Notification hub description: new unconfigured products detected',
  },
  ORDER_ALERT_LOW_INVENTORY: {
    id: 'lU99Xv',
    defaultMessage:
      'The product {productName} has reached or gone below the reorder threshold ({thresholdValue}) established as a warning.',
    description: 'Notification hub description: product stock has reached or fallen below the reorder threshold',
  },
  // ORDER_ALERT_AUTOMATION_RULE intentionally has no descriptor here —
  // the description comes from the automation rule itself and is stored in
  // notification.description directly, so the component renders it as a fallback.

  // === CSV Import ===
  CSV_IMPORT_COMPLETED: {
    id: '2bhPSa',
    defaultMessage:
      'Your {importType} import finished successfully. ' +
      '{totalRecords, plural, one {# item} other {# items}} imported. ' +
      '{processed} succeeded and {failedCount} failed.' +
      '{warningsCount, plural, =0 {} one { # warning.} other { # warnings.}}',
    description: 'Notification hub description: CSV import completed with type, totals, and counts',
  },
  CSV_IMPORT_COMPLETED_WITH_WARNINGS: {
    id: '8GDe8C',
    defaultMessage:
      'Your {importType} import finished with warnings. ' +
      '{totalRecords, plural, one {# item} other {# items}} imported. ' +
      '{processed} succeeded and {failedCount} failed.' +
      '{warningsCount, plural, =0 {} one { # warning.} other { # warnings.}}',
    description: 'Notification hub description: CSV import completed with type, warnings, totals, and counts',
  },
  CSV_IMPORT_FAILED: {
    id: 'fnucJq',
    defaultMessage: 'Your {importType} import failed unexpectedly. Please try again or contact support.',
    description: 'Notification hub description: CSV import failed with type',
  },

  // === Orders Import ===
  ORDERS_IMPORTED: {
    id: 'WIs8UI',
    defaultMessage:
      'Your order import finished successfully.' +
      '{failedCount, plural, =0 {} one { # order failed to import.} other { # orders failed to import.}}',
    description: 'Notification hub description: orders imported with optional error count',
  },

  // === Customers Import ===
  CUSTOMERS_IMPORTED: {
    id: 'ewvUxI',
    defaultMessage:
      'Your customer import finished successfully.' +
      '{failedCount, plural, =0 {} one { # customer failed to import.} other { # customers failed to import.}}',
    description: 'Notification hub description: customer import completed with optional error count',
  },
  CUSTOMERS_IMPORT_FAILED: {
    id: 'n1dNM7',
    defaultMessage: 'Your customer import failed. Please try again or contact support.',
    description: 'Notification hub description: customer import failed',
  },

  // === Orders Export ===
  ORDERS_EXPORT_STARTED: {
    id: 'i6M9i3',
    defaultMessage: 'Your Order Export is being generated.',
    description: 'Notification hub description: order export in progress',
  },
  ORDERS_EXPORT_COMPLETED: {
    id: '5rRb+g',
    defaultMessage: "Your order export is ready for download. If download didn't start automatically, click button below.",
    description: 'Notification hub description: order export finished with download link',
  },
  ORDERS_EXPORT_FAILED: {
    id: 'nQSJcF',
    defaultMessage: 'An error occurred while attempting to export. Please try again.',
    description: 'Notification hub description: order export failed',
  },

  // === Shipments Export ===
  SHIPMENTS_EXPORT_STARTED: {
    id: '6RuayH',
    defaultMessage: 'Your Shipment Export is being generated.',
    description: 'Notification hub description: shipment export in progress',
  },
  SHIPMENTS_EXPORT_COMPLETED: {
    id: 'myqhPV',
    defaultMessage: "Your shipment export is ready for download. If download didn't start automatically, click button below.",
    description: 'Notification hub description: shipment export finished with download link',
  },
  SHIPMENTS_EXPORT_FAILED: {
    id: 'F4AR7L',
    defaultMessage: 'An error occurred while attempting to export. Please try again.',
    description: 'Notification hub description: shipment export failed',
  },

  // === Freight Export ===
  FREIGHT_EXPORT_STARTED: {
    id: 'vXwcno',
    defaultMessage: 'Your Freight Shipment Export is being generated.',
    description: 'Notification hub description: freight shipment export in progress',
  },
  FREIGHT_EXPORT_COMPLETED: {
    id: 'GfzZH7',
    defaultMessage: "Your freight shipment export is ready for download. If download didn't start automatically, click button below.",
    description: 'Notification hub description: freight shipment export finished with download link',
  },
  FREIGHT_EXPORT_FAILED: {
    id: 'DOP62f',
    defaultMessage: 'An error occurred while attempting to export. Please try again.',
    description: 'Notification hub description: freight shipment export failed',
  },

  // === Product Import ===
  PRODUCT_IMPORT_COMPLETED: {
    id: '0tOnha',
    defaultMessage:
      'Your product import finished successfully.' +
      '{failedCount, plural, =0 {} one { # product failed to import.} other { # products failed to import.}}',
    description: 'Notification hub description: product import completed with optional error count',
  },
  PRODUCT_ALIAS_IMPORT_COMPLETED: {
    id: 'LTGrYY',
    defaultMessage:
      'Your product alias import finished successfully.' +
      '{failedCount, plural, =0 {} one { # alias failed to import.} other { # aliases failed to import.}}',
    description: 'Notification hub description: product alias import completed with optional error count',
  },
  PRODUCT_BUNDLES_IMPORT_COMPLETED: {
    id: 'YOrGr9',
    defaultMessage:
      'Your product bundles import finished successfully.' +
      '{failedCount, plural, =0 {} one { # bundle failed to import.} other { # bundles failed to import.}}',
    description: 'Notification hub description: product bundles import completed with optional error count',
  },
  PRODUCT_IMPORT_FAILED: {
    id: 'M8ParW',
    defaultMessage: 'Your product import failed. Please try again or contact support.',
    description: 'Notification hub description: product import failed',
  },

  // === Product Export ===
  PRODUCT_EXPORT_COMPLETED: {
    id: 'fQrAHX',
    defaultMessage: "Your product export is ready for download. If download didn't start automatically, click button below.",
    description: 'Notification hub description: product export completed with download link',
  },
  PRODUCT_EXPORT_FAILED: {
    id: 'pXBdL1',
    defaultMessage: 'An error occurred while exporting products.',
    description: 'Notification hub description: product export failed',
  },

  // === Inventory Import ===
  INVENTORY_IMPORT_COMPLETED: {
    id: 'tklfPS',
    defaultMessage: 'Your inventory import finished successfully.',
    description: 'Notification hub description: inventory import completed',
  },
  INVENTORY_IMPORT_FAILED: {
    id: 'iIimDG',
    defaultMessage: 'Your inventory import failed. Please try again or contact support.',
    description: 'Notification hub description: inventory import failed',
  },

  // === Inventory Export ===
  INVENTORY_EXPORT_COMPLETED: {
    id: 'r7356K',
    defaultMessage: "Your inventory export is ready for download. If download didn't start automatically, click button below.",
    description: 'Notification hub description: inventory export completed with download link',
  },
  INVENTORY_EXPORT_FAILED: {
    id: 'X6nj/j',
    defaultMessage: 'Your inventory export has failed.',
    description: 'Notification hub description: inventory export failed',
  },

  // === Locations Export ===
  LOCATIONS_EXPORT_STARTED: {
    id: 'zIBhPc',
    defaultMessage: 'Your inventory locations export is being generated.',
    description: 'Notification hub description: locations export started',
  },
  LOCATIONS_EXPORT_COMPLETED: {
    id: 'Vc7Qh+',
    defaultMessage: "Your locations export is ready for download. If download didn't start automatically, click button below.",
    description: 'Notification hub description: locations export completed with download link',
  },
  LOCATIONS_EXPORT_FAILED: {
    id: 'NbYkZZ',
    defaultMessage: 'Your inventory locations export has failed.',
    description: 'Notification hub description: locations export failed',
  },

  // === Totes Export ===
  TOTES_EXPORT_STARTED: {
    id: 'BgCaws',
    defaultMessage: 'Your totes export is being generated.',
    description: 'Notification hub description: totes export started',
  },
  TOTES_EXPORT_COMPLETED: {
    id: 'dDIZV8',
    defaultMessage: "Your totes export is ready for download. If download didn't start automatically, click button below.",
    description: 'Notification hub description: totes export completed with download link',
  },
  TOTES_EXPORT_FAILED: {
    id: 'tiZXmA',
    defaultMessage: 'Your totes export has failed.',
    description: 'Notification hub description: totes export failed',
  },

  // === Custom Packages Export ===
  CUSTOM_PACKAGES_EXPORT_STARTED: {
    id: 'dLbTqr',
    defaultMessage: 'Your custom packages export is being generated.',
    description: 'Notification hub description: custom packages export started',
  },
  CUSTOM_PACKAGES_EXPORT_COMPLETED: {
    id: 's0YFaC',
    defaultMessage: "Your custom packages export is ready for download. If download didn't start automatically, click button below.",
    description: 'Notification hub description: custom packages export completed with download link',
  },
  CUSTOM_PACKAGES_EXPORT_FAILED: {
    id: 'aIdTuD',
    defaultMessage: 'Your custom packages export has failed. Our team has been notified of this issue.',
    description: 'Notification hub description: custom packages export failed',
  },

  // === Forecasting Export ===
  FORECASTING_EXPORT_FAILED: {
    id: 'lnuSar',
    defaultMessage: 'CSV download failed.',
    description: 'Notification hub description: forecasting export failed',
  },

  // === Fulfillments Export ===
  FULFILLMENTS_EXPORT_STARTED: {
    id: '8FV/zb',
    defaultMessage: 'Your fulfillments export is being generated.',
    description: 'Notification hub description: fulfillments export in progress',
  },
  FULFILLMENTS_EXPORT_COMPLETED: {
    id: 'nnrmTU',
    defaultMessage: "Your fulfillments export is ready for download. If download didn't start automatically, click button below.",
    description: 'Notification hub description: fulfillments export completed with download link',
  },
  FULFILLMENTS_EXPORT_FAILED: {
    id: 'CKi+Q6',
    defaultMessage: 'An error occurred while attempting to export fulfillments. Please try again.',
    description: 'Notification hub description: fulfillments export failed',
  },

  // === Transaction History Export ===
  TRANSACTION_HISTORY_EXPORT_STARTED: {
    id: 'O26rGB',
    defaultMessage: 'Your transaction history export is being generated.',
    description: 'Notification hub description: transaction history export in progress',
  },
  TRANSACTION_HISTORY_EXPORT_COMPLETED: {
    id: '4sTFQ0',
    defaultMessage: "Your transaction history export is ready for download. If download didn't start automatically, click button below.",
    description: 'Notification hub description: transaction history export completed with download link',
  },
  TRANSACTION_HISTORY_EXPORT_FAILED: {
    id: 'pCOqAK',
    defaultMessage: 'An error occurred while attempting to export transaction history. Please try again.',
    description: 'Notification hub description: transaction history export failed',
  },

  // === Returns Export ===
  RETURNS_EXPORT_COMPLETED: {
    id: '68vZfV',
    defaultMessage: "Your returns export is ready for download. If download didn't start automatically, click button below.",
    description: 'Notification hub description: returns export completed with download link',
  },
});
